/**
 * The markdown subset a release-note BODY may use, parsed into a small block
 * structure that each surface renders its own way:
 *
 *   - the app (src/components/release-notes/ReleaseNotesList.jsx) emits React
 *     elements from parseNoteBody() — bundled in at build time, so the runtime
 *     never depends on scripts/ being deployed (same rule as paths.js);
 *   - the website publisher (publish-website.js) emits HTML via renderBodyHtml().
 *
 * The subset is exactly what the published corpus uses: blank-line paragraphs,
 * `- ` bullets (column 0) with indented continuation lines, **bold**, *italic*
 * and inline `code`. A single newline inside a paragraph or bullet is a
 * cosmetic source wrap and joins to a space (CommonMark soft-break semantics) —
 * that join is the fix for hard-wrapped bodies rendering with mid-sentence
 * line breaks (TGCLOUD-1746).
 *
 * Outside the subset the parser is deliberately conservative: emphasis is only
 * recognised when its markers are cleanly delimited (see INLINE_RE), so
 * near-misses — nested emphasis, ***triple***, intraword asterisks like 2*3 —
 * stay literal rather than italicising half a sentence. What it does NOT
 * promise is structural preservation: soft-joining means an unrecognised
 * layout (an indented or nested bullet, say) keeps its characters but loses
 * its line breaks. validate.js warns about those shapes at authoring time.
 *
 * ESM on purpose: the frontend imports it natively, and the CommonJS tooling
 * loads it with require(esm) — supported from Node 22.12; CI runs 24.x. Keep it
 * dependency-free and free of top-level await (require(esm) rejects TLA).
 * notes.js deliberately does not load this module: it keeps itself runnable on
 * any Node with no install step (its own header), and its renderMarkdown emits
 * markdown for GitHub to render — it needs the line join, not this parser.
 * validate.js mirrors INLINE_RE for the same reason; keep the two in sync.
 */

/**
 * @typedef {{kind: 'text'|'bold'|'italic'|'code', text: string}} Run
 * @typedef {{kind: 'paragraph', runs: Run[]} | {kind: 'list', items: Run[][]}} Block
 */

// Bold before italic so `**` is never read as two italic markers. Both
// emphasis forms demand clean flanking — nothing word-like or another `*`
// touching the outside of a marker, no whitespace just inside it — which is
// what keeps `2*3`, `***triple***`, `** spaced **` and the stranded halves of
// nested emphasis literal instead of italicising across words (the failure a
// naive pattern produces). Mirrored in validate.js; keep the two in sync.
const INLINE_RE =
  /(?<![\w*])\*\*(?!\s)([^*]+?)(?<![\s*])\*\*(?![\w*])|(?<![\w*])\*(?!\s)([^*\n]+?)(?<![\s*])\*(?![\w*])|`([^`\n]+?)`/g;

const LIST_ITEM_RE = /^-\s+/;

/** @param {string} text one logical line, already soft-joined @returns {Run[]} */
export function parseInline(text) {
  const runs = [];
  let last = 0;
  let match;
  INLINE_RE.lastIndex = 0;
  while ((match = INLINE_RE.exec(text))) {
    if (match.index > last) {
      runs.push({ kind: 'text', text: text.slice(last, match.index) });
    }
    if (match[1] !== undefined) runs.push({ kind: 'bold', text: match[1] });
    else if (match[2] !== undefined)
      runs.push({ kind: 'italic', text: match[2] });
    else runs.push({ kind: 'code', text: match[3] });
    last = match.index + match[0].length;
  }
  if (last < text.length) runs.push({ kind: 'text', text: text.slice(last) });
  return runs;
}

/**
 * @param {string} body a note body as frozen in releases/<version>.json
 * @returns {Block[]}
 */
export function parseNoteBody(body) {
  const text = String(body || '').replace(/\r\n/g, '\n');
  /** @type {Block[]} */
  const blocks = [];
  // Segment state survives only within a blank-line-separated chunk, so a
  // blank line always closes the current paragraph or list.
  for (const chunk of text.split(/\n[ \t]*\n+/)) {
    if (!chunk.trim()) continue;
    let current = null;
    for (const line of chunk.split('\n')) {
      if (LIST_ITEM_RE.test(line)) {
        if (!current || current.kind !== 'list') {
          current = { kind: 'list', rawItems: [] };
          blocks.push(current);
        }
        current.rawItems.push(line.replace(LIST_ITEM_RE, '').trim());
      } else if (current?.kind === 'list' && /^\s/.test(line)) {
        // An indented line under a bullet continues that item — this is the
        // shape the corpus wraps long bullets with (2-space continuations).
        current.rawItems[current.rawItems.length - 1] += ` ${line.trim()}`;
      } else {
        if (!current || current.kind !== 'paragraph') {
          current = { kind: 'paragraph', rawLines: [] };
          blocks.push(current);
        }
        current.rawLines.push(line.trim());
      }
    }
  }
  return blocks.map((block) =>
    block.kind === 'list'
      ? { kind: 'list', items: block.rawItems.map(parseInline) }
      : { kind: 'paragraph', runs: parseInline(block.rawLines.join(' ')) }
  );
}

const escapeHtml = (value) =>
  String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

const renderRunsHtml = (runs) =>
  runs
    .map((run) => {
      if (run.kind === 'bold')
        return `<strong>${escapeHtml(run.text)}</strong>`;
      if (run.kind === 'italic') return `<em>${escapeHtml(run.text)}</em>`;
      if (run.kind === 'code') return `<code>${escapeHtml(run.text)}</code>`;
      return escapeHtml(run.text);
    })
    .join('');

/**
 * The body as an HTML fragment, for the website accordion. Inline margins
 * because the fragment lands inside an Elementor widget whose stylesheet we do
 * not control.
 *
 * @param {string} body
 * @returns {string} empty when the body parses to nothing
 */
export function renderBodyHtml(body) {
  return parseNoteBody(body)
    .map((block) =>
      block.kind === 'list'
        ? `<ul style="margin:6px 0 6px 18px;padding:0;">${block.items
            .map((item) => `<li>${renderRunsHtml(item)}</li>`)
            .join('')}</ul>`
        : `<p style="margin:6px 0;">${renderRunsHtml(block.runs)}</p>`
    )
    .join('');
}
